/**
 * @file invoke端内调起
 * @author yanyiting@baidu.com
 */

import {isIOS, isAndroid} from '@baidu/xbox/os';
import {NOT_IN_APP, COMMAND_FAIL, COMMAND_RES_ERROR, COMMAND_NOT_EXIST} from './const';
import {isPlainObject} from '@baidu/xbox/is';

/**
 * 传入params调用端能力
 * @function androidInvoke
 * @param {string} params 协议参数字符串对象
 * @return {Object} 发起postMessage请求
 */

export const androidInvoke = (params, jsBridge) => {
    if (typeof window !== 'object') {
        return;
    }
    return window[jsBridge] && window[jsBridge].postMessage(params);
};

/**
 * 传入params调用端能力
 * @function iosInvoke
 * @param {string} params 协议参数字符串对象
 * @return {Object} 发起postMessage请求
 */

export const iosInvoke = (params, jsBridge) => {
    if (typeof window !== 'object') {
        return;
    }
    return window.webkit && window.webkit.messageHandlers[jsBridge].postMessage(params);
};

/**
 * 统一处理返回的结果
 * @function resHandle
 * @param {Object} res 返回结果
 * @return {Object} 统一结果格式
 */

export const resHandle = res => {
    let {errno, errmsg, data} = COMMAND_RES_ERROR;

    if (!isPlainObject(res)) {
        try {
            res = JSON.parse(res);
            errno = +res.errno;
            errmsg = res.errmsg;
            data = res.data || {};
        }
        catch (e) {
            /* eslint-disable no-console */
            console.log(e);
            /* eslint-enable no-console */
        }
    }
    return {
        errno,
        errmsg,
        data
    };
};

/**
 * invoke传入params调用端能力
 * @function default
 * @param {Object} paramObj 协议参数字符串对象, params为对象无需JSON.stringify转换
 * @param {Object} other 其他参数
 * @param {Object} other.jsBridge - 与客户端约定的jsBridge取值
 * @param {Object} other.inBox - 是否在app内
 * @return {Promise}
 * 注意: 通常情况下不建议直接调用invoke，优先使用register指定端能力支持协议版本调用
 */

export default function invoke({module, action, params = {}}, {jsBridge, inBox}) {
    return new Promise((resolve, reject) => {
        if (!inBox) {
            reject(NOT_IN_APP);
            return;
        }

        if (!(module && action)) {
            reject(COMMAND_NOT_EXIST);
            return;
        }
        /* eslint-disable dot-notation */
        window['__fc__callback__'] = result => {
        /* eslint-disable dot-notation */
            const res = resHandle(result);
            if (+res.errno === 0) {
                resolve(res.data);
                return;
            }
            reject(res);
        };
        const paramsStr = JSON.stringify({
            module,
            action,
            params,
            callback: '__fc__callback__'
        });

        if (isIOS()) {
            return iosInvoke(paramsStr, jsBridge);
        }
        else if (isAndroid()) {
            return androidInvoke(paramsStr, jsBridge);
        }

        reject(COMMAND_FAIL);
    });
}

/**
 * invokeAsync传入params调用端能力
 * @function default
 * @param {Object} paramObj 协议参数字符串对象, params为对象无需JSON.stringify转换
 * 注意: 通常情况下不建议直接调用invoke，优先使用register指定端能力支持协议版本调用
 */
window['__fc_default__callback__'] = () => {};
export function invokeAsync({module, action, params = {}, callback}, {jsBridge, inBox}) {
    if (!inBox) {
        return;
    }

    if (!(module && action)) {
        return;
    }
    const paramsStr = JSON.stringify({
        module,
        action,
        params,
        callback: callback || '__fc_default__callback__'
    });

    if (isIOS()) {
        return iosInvoke(paramsStr, jsBridge);
    }
    else if (isAndroid()) {
        return androidInvoke(paramsStr, jsBridge);
    }
}
